const OVERLAP_BUFFER_PX = 2;

/**
 * Thins out SVG labels so that no two visible ones overlap.
 *
 * Labels marked "data-force-show" are resolved first, so a forced label only
 * loses to a forced label before it; every other label loses to any label
 * already accepted. Labels marked "data-hide-parent" are always hidden,
 * through their parent group so the tick line goes with them.
 *
 * Runs again on every resize, so it clears its own previous writes before
 * measuring: a label left at "display: none" measures as an empty box, and
 * would stay hidden forever once the chart grows wide enough to fit it.
 */
export const controlVisibility = (elements: Element[]): void => {
  const svgElements = elements.filter(isSVGElement);

  if (!svgElements.length) {
    return;
  }

  const candidates: LabelCandidate[] = svgElements.map((element) => ({
    parent: getSVGParent(element),
    element: element,
    hideParent: element.getAttribute('data-hide-parent') === 'true',
    forceShow: element.getAttribute('data-force-show') === 'true',
  }));

  clearInlineDisplay(candidates);

  const labels = measureLabels(candidates);

  // A chart measured before layout has no boxes to compare, so overlaps cannot
  // be resolved. Fall back to the forced set: useChartObserver reveals the chart
  // either way, and showing everything would draw the labels stacked.
  if (!labels.some((label) => label.isMeasurable)) {
    for (const label of labels) {
      label.visible = label.forceShow;
    }
    applyVisibility(labels);
    return;
  }

  const visibleLabels: LabelInfo[] = [];
  revealWithoutOverlap(
    labels.filter((label) => label.forceShow),
    visibleLabels,
  );
  revealWithoutOverlap(
    labels.filter((label) => !label.forceShow),
    visibleLabels,
  );

  applyVisibility(labels);
};

interface LabelCandidate {
  parent: SVGElement | null;
  element: SVGElement;
  hideParent: boolean;
  forceShow: boolean;
}

interface LabelInfo extends LabelCandidate {
  rect: DOMRect;
  isMeasurable: boolean;
  visible: boolean;
}

/** Writes every element before reading any, to cost a single reflow. */
const clearInlineDisplay = (candidates: LabelCandidate[]): void => {
  for (const candidate of candidates) {
    candidate.element.style.display = '';
    if (candidate.parent) {
      candidate.parent.style.display = '';
    }
  }
};

const measureLabels = (candidates: LabelCandidate[]): LabelInfo[] =>
  candidates.map((candidate) => {
    const rect = candidate.element.getBoundingClientRect();

    return {
      ...candidate,
      rect: rect,
      isMeasurable: rect.width > 0 && rect.height > 0,
      visible: false,
    };
  });

/**
 * Accepts labels in the order given, each one only if it clears every label
 * accepted so far. That order is the caller's array, not a sort by position:
 * axis ticks arrive in DOM order, value labels one series after another, so a
 * colliding pair is decided by array position rather than by which sits left.
 *
 * A label without a box is left hidden and never joins visibleLabels, so it
 * cannot block the labels that do have one.
 */
const revealWithoutOverlap = (
  labels: LabelInfo[],
  visibleLabels: LabelInfo[],
): void => {
  for (const label of labels) {
    if (label.hideParent || !label.isMeasurable) continue;

    const hasOverlap = visibleLabels.some((visibleLabel) =>
      overlaps(label.rect, visibleLabel.rect),
    );
    if (hasOverlap) continue;

    label.visible = true;
    visibleLabels.push(label);
  }
};

const overlaps = (rect: DOMRect, otherRect: DOMRect): boolean =>
  !(
    rect.right + OVERLAP_BUFFER_PX < otherRect.left ||
    rect.left > otherRect.right + OVERLAP_BUFFER_PX ||
    rect.bottom + OVERLAP_BUFFER_PX < otherRect.top ||
    rect.top > otherRect.bottom + OVERLAP_BUFFER_PX
  );

const applyVisibility = (labels: LabelInfo[]): void => {
  for (const label of labels) {
    const element = label.hideParent ? label.parent : label.element;
    if (!element) {
      console.warn('Parent is null', label);
      continue;
    }
    // Restores the stylesheet default rather than forcing a display mode.
    element.style.display = label.visible ? '' : 'none';
  }
};

const getSVGParent = (element: SVGElement): SVGElement | null =>
  isSVGElement(element.parentElement) ? element.parentElement : null;

const isSVGElement = (node: Element | null): node is SVGElement => {
  if (!(node instanceof SVGElement)) {
    console.warn('Node is not SVGElement', node);
    return false;
  }
  return true;
};
